import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import FlatButton from 'material-ui/FlatButton';
import MenuItem from 'material-ui/MenuItem';
import SelectField from 'material-ui/SelectField';
import Toggle from 'material-ui/Toggle';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';

import api from 'services';

/** Bin widths in days. The chart picks the smallest one that still keeps about eight bins. */
const NICE_WIDTHS_DAYS = [0.25, 0.5, 1, 2, 5];
const TARGET_BINS = 8;
/** Narrowest a bin's column may be. Past this the chart scrolls instead of squeezing the bars. */
const MIN_COLUMN = 52;
/** Vessels above this percentile are dropped while the trim toggle is on. */
const TAIL_PERCENTILE = 0.95;
const ALL_VESSELS = 'all';

const VESSEL_TYPE_LABELS = {
    bulk: 'Bulker Carrier',
    bulktank: 'Ore/Bulk/Oil Carrier',
    car: 'Car Carrier',
    cont: 'Container Carrier',
    livestock: 'Livestock Carrier',
    mpp: 'Multipurpose and HeavyLift Carrier',
    pass: 'Passenger Carrier',
    passcar: 'Passenger Car Carrier',
    reefer: 'Reefer Carrier',
    tank: 'Liquid Bulk Carrier',
    unknown: 'Unknown',
};

function vesselTypeLabel(type) {
    return VESSEL_TYPE_LABELS[type] || type || 'Unknown';
}

/**
 * Smallest nice width at least as wide as the span divided into {@link TARGET_BINS}.
 * Wider than every nice width falls back to the widest.
 */
function chooseWidth(min, max) {
    const span = Math.max(0, max - min);
    if (span === 0) {
        return NICE_WIDTHS_DAYS[0];
    }
    const ideal = span / TARGET_BINS;
    for (let i = 0; i < NICE_WIDTHS_DAYS.length; i += 1) {
        if (NICE_WIDTHS_DAYS[i] >= ideal) {
            return NICE_WIDTHS_DAYS[i];
        }
    }
    return NICE_WIDTHS_DAYS[NICE_WIDTHS_DAYS.length - 1];
}

/** Nearest-rank cutoff. Vessels with more days than this are the upper tail. */
function tailCutoff(vessels, percentile) {
    const days = vessels.map(vessel => vessel.days).sort((a, b) => a - b);
    const index = Math.ceil(percentile * days.length) - 1;
    return days[Math.min(days.length - 1, Math.max(0, index))];
}

function buildBins(vessels) {
    if (!vessels.length) {
        return { width: NICE_WIDTHS_DAYS[0], start: 0, bins: [] };
    }
    let min = vessels[0].days;
    let max = vessels[0].days;
    vessels.forEach((vessel) => {
        if (vessel.days < min) min = vessel.days;
        if (vessel.days > max) max = vessel.days;
    });
    const width = chooseWidth(min, max);
    const start = Math.floor(min / width) * width;
    const count = Math.max(1, Math.floor((max - start) / width) + 1);
    const bins = [];
    for (let index = 0; index < count; index += 1) {
        bins.push({
            from: start + (index * width),
            to: start + ((index + 1) * width),
            vessels: [],
        });
    }
    vessels.forEach((vessel) => {
        const index = Math.min(count - 1, Math.max(0, Math.floor((vessel.days - start) / width)));
        bins[index].vessels.push(vessel);
    });
    return { width, start, bins };
}

function edgeLabel(days, width) {
    if (width < 1) {
        const hours = Math.round(days * 24);
        return hours === 0 ? '0' : `${hours}h`;
    }
    return String(days);
}

function widthLabel(width) {
    if (width < 1) {
        return `${Math.round(width * 24)} hours`;
    }
    return width === 1 ? '1 day' : `${width} days`;
}

function formatDays(days) {
    if (days < 1) {
        return days.toFixed(2);
    }
    return days.toFixed(1);
}

function yStep(max) {
    const rough = max / 5;
    if (rough <= 1) {
        return 1;
    }
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const fraction = rough / magnitude;
    if (fraction >= 5) {
        return 5 * magnitude;
    }
    if (fraction >= 2) {
        return 2 * magnitude;
    }
    return magnitude;
}

function HistogramChart({
    bins,
    width,
    selectedIndex,
    onSelect,
}) {
    const frame = useRef(null);
    const [frameWidth, setFrameWidth] = useState(0);

    useEffect(() => {
        const node = frame.current;
        if (!node) {
            return undefined;
        }
        const measure = () => setFrameWidth(node.clientWidth);
        measure();
        if (typeof window.ResizeObserver !== 'function') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new window.ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const svgHeight = 300;
    const pad = {
        top: 18,
        right: 8,
        bottom: 64,
        left: 48,
    };
    const columnFloor = (pad.left + pad.right) + (bins.length * MIN_COLUMN);
    const svgWidth = Math.max(frameWidth, columnFloor);
    const plotWidth = (svgWidth - pad.left) - pad.right;
    const plotHeight = (svgHeight - pad.top) - pad.bottom;
    const maxVessels = bins.reduce((top, bin) => Math.max(top, bin.vessels.length), 0);
    const yMax = Math.max(1, maxVessels);
    const step = yStep(yMax);
    const slot = bins.length ? plotWidth / bins.length : 0;
    const barWidth = Math.max(8, slot - 8);
    const ticks = [];
    for (let value = 0; value <= yMax; value += step) {
        const y = (pad.top + plotHeight) - ((value / yMax) * plotHeight);
        ticks.push({ value, y });
    }

    return (
        <div className='congestion-histogram-chart' ref={frame}>
            {!!bins.length && (
                <svg width={svgWidth} height={svgHeight} role='img' aria-label='Vessels by days of congestion'>
                    {ticks.map(tick => (
                        <g key={tick.value}>
                            <line x1={pad.left} y1={tick.y} x2={svgWidth - pad.right} y2={tick.y} stroke='#e0e0e0' />
                            <text x={pad.left - 6} y={tick.y + 4} textAnchor='end' fontSize='11' fill='#666'>{tick.value}</text>
                        </g>
                    ))}
                    <line
                        x1={pad.left}
                        y1={pad.top + plotHeight}
                        x2={svgWidth - pad.right}
                        y2={pad.top + plotHeight}
                        stroke='#333'
                    />
                    <text
                        x={pad.left + (plotWidth / 2)}
                        y={svgHeight - 4}
                        textAnchor='middle'
                        fontSize='12'
                        fill='#333'
                    >
                Days of congestion
                    </text>
                    <text
                        x={14}
                        y={pad.top + (plotHeight / 2)}
                        textAnchor='middle'
                        fontSize='12'
                        fill='#333'
                        transform={`rotate(-90 14 ${pad.top + (plotHeight / 2)})`}
                    >
                Vessels
                    </text>
                    {bins.map((bin, index) => {
                        const barHeight = (bin.vessels.length / yMax) * plotHeight;
                        const barOffset = (slot - barWidth) / 2;
                        const x = (pad.left + (index * slot)) + barOffset;
                        const y = (pad.top + plotHeight) - barHeight;
                        const label = `${edgeLabel(bin.from, width)}–${edgeLabel(bin.to, width)}`;
                        const selected = index === selectedIndex;
                        return (
                            <g key={label}>
                                <rect
                                    className='congestion-histogram-bar'
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={Math.max(barHeight, 0)}
                                    fill={selected ? '#00bcd4' : '#12699b'}
                                    onClick={() => onSelect(index)}
                                >
                                    <title>{`${label}: ${bin.vessels.length} vessels`}</title>
                                </rect>
                                <text
                                    x={x + (barWidth / 2)}
                                    y={svgHeight - 28}
                                    textAnchor='middle'
                                    fontSize='11'
                                    fill='#333'
                                    transform={`rotate(-40 ${x + (barWidth / 2)} ${svgHeight - 28})`}
                                >
                                    {label}
                                </text>
                                {bin.vessels.length > 0 && (
                                    <text x={x + (barWidth / 2)} y={y - 4} textAnchor='middle' fontSize='11' fill='#333'>
                                        {bin.vessels.length}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            )}
        </div>
    );
}

export default function CongestionHistogram({ portId, range, onShowTrack }) {
    const [vesselType, setVesselType] = useState(ALL_VESSELS);
    const [excludeTail, setExcludeTail] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const histogram = useQuery(
        ['congestionHistogram', portId, range.dateFrom, range.dateTo],
        () => api.getCongestionHistogram(portId, range),
        { enabled: !!portId },
    );

    const vessels = (histogram.data && histogram.data.vessels) || [];
    const types = useMemo(() => {
        const present = {};
        vessels.forEach((vessel) => { present[vessel.vesselType || 'unknown'] = true; });
        return Object.keys(present).sort((a, b) => vesselTypeLabel(a).localeCompare(vesselTypeLabel(b)));
    }, [vessels]);

    useEffect(() => {
        setVesselType(ALL_VESSELS);
        setSelectedIndex(null);
    }, [portId, range.dateFrom, range.dateTo]);

    const shown = useMemo(() => (
        vesselType === ALL_VESSELS
            ? vessels
            : vessels.filter(vessel => (vessel.vesselType || 'unknown') === vesselType)
    ), [vessels, vesselType]);
    const plotted = useMemo(() => {
        if (!excludeTail || !shown.length) {
            return { vessels: shown, cutoff: null, excluded: 0 };
        }
        const cutoff = tailCutoff(shown, TAIL_PERCENTILE);
        const kept = shown.filter(vessel => vessel.days <= cutoff);
        return { vessels: kept, cutoff, excluded: shown.length - kept.length };
    }, [shown, excludeTail]);
    const { width, bins } = useMemo(() => buildBins(plotted.vessels), [plotted]);
    const selected = selectedIndex != null ? bins[selectedIndex] : null;
    const rows = selected ? selected.vessels : plotted.vessels;

    const selectBar = (index) => {
        setSelectedIndex(current => (current === index ? null : index));
    };

    const changeType = (event, index, value) => {
        setVesselType(value);
        setSelectedIndex(null);
    };

    return (
        <div className='congestion-histogram-split'>
            <div className='congestion-histogram-panel'>
                <div className='congestion-histogram-head'>
                    <div>
                        <b>Congestion histogram</b>
                        <div className='congestion-histogram-note'>
                            {histogram.isFetching && 'Loading…'}
                            {!histogram.isFetching && plotted.vessels.length > 0 && (
                                `${plotted.vessels.length} vessels · bins of ${widthLabel(width)}`
                            )}
                            {!histogram.isFetching && plotted.excluded > 0 && (
                                ` · ${plotted.excluded} above ${formatDays(plotted.cutoff)} days hidden`
                            )}
                            {!histogram.isFetching && !shown.length && 'No completed waits in this period.'}
                            {histogram.data && histogram.data.truncated && ' · sample was capped'}
                        </div>
                    </div>
                    <div className='congestion-histogram-controls'>
                        <Toggle
                            label='Exclude above 95th pctl.'
                            labelPosition='right'
                            toggled={excludeTail}
                            onToggle={(event, checked) => {
                                setExcludeTail(checked);
                                setSelectedIndex(null);
                            }}
                            className='congestion-draw-toggle'
                        />
                        <SelectField
                            floatingLabelText='Vessels'
                            value={vesselType}
                            onChange={changeType}
                            style={{ width: 260 }}
                        >
                            <MenuItem value={ALL_VESSELS} primaryText='All vessels' />
                            {types.map(type => (
                                <MenuItem key={type} value={type} primaryText={vesselTypeLabel(type)} />
                            ))}
                        </SelectField>
                    </div>
                </div>
                <HistogramChart
                    bins={bins}
                    width={width}
                    selectedIndex={selectedIndex}
                    onSelect={selectBar}
                />
            </div>
            <div className='congestion-histogram-panel'>
                <div className='congestion-histogram-note'>
                    {selected
                        ? `${rows.length} vessels in ${edgeLabel(selected.from, width)}–${edgeLabel(selected.to, width)}`
                        : `${rows.length} vessels, longest wait first. Click a bar to narrow the list.`}
                </div>
                <div className='congestion-histogram-vessels'>
                    {!rows.length && (
                        <p className='congestion-empty'>
                            {selected ? 'No vessels in this bin.' : 'No completed waits in this period.'}
                        </p>
                    )}
                    {!!rows.length && (
                        <Table selectable={false} fixedHeader={false}>
                            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                <TableRow>
                                    <TableHeaderColumn>MMSI</TableHeaderColumn>
                                    <TableHeaderColumn>Vessel</TableHeaderColumn>
                                    <TableHeaderColumn>Type</TableHeaderColumn>
                                    <TableHeaderColumn className='num'>Days</TableHeaderColumn>
                                    <TableHeaderColumn>Track</TableHeaderColumn>
                                </TableRow>
                            </TableHeader>
                            <TableBody displayRowCheckbox={false}>
                                {rows.map(vessel => (
                                    <TableRow key={vessel.mmsi}>
                                        <TableRowColumn>{vessel.mmsi}</TableRowColumn>
                                        <TableRowColumn>{vessel.vesselName || '—'}</TableRowColumn>
                                        <TableRowColumn>{vesselTypeLabel(vessel.vesselType)}</TableRowColumn>
                                        <TableRowColumn className='num'>{formatDays(vessel.days)}</TableRowColumn>
                                        <TableRowColumn>
                                            <FlatButton label='Show' onClick={() => onShowTrack(vessel.mmsi)} />
                                        </TableRowColumn>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/Congestion/CongestionHistogram.jsx